import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../ui/sonner';
import { messageErreur } from '../../lib/erreurs';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import { lignesPourEnvoi, resumeGrille } from '../../lib/lotsCollage';
import GrilleCamions from './GrilleCamions';
import { ListeRefus } from './lotsUi';

const lignesRefus = (liste = []) =>
  liste.map((ligne) => ({ plaque: ligne.tractor_plate || (ligne.row ? `Ligne ${ligne.row}` : ''), message: ligne.reason }));

/**
 * Ajoute des camions à un lot ouvert : même grille que la création (lignes
 * collées ou saisies). Les plaques déjà dans le lot et les lignes illisibles
 * sont rendues avec leur phrase du serveur.
 */
export const AjouterCamionsDialog = ({ open, onOpenChange, lotId, reference, onDone }) => {
  const [lignes, setLignes] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState(null);
  const cle = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLignes([]);
    setResultat(null);
    setEnCours(false);
    cle.current = nouvelleCleLot('lot-camions');
  }, [open]);

  const resume = resumeGrille(lignes);

  const envoyer = async (evenement) => {
    evenement.preventDefault();
    if (enCours || resume.total === 0 || resume.sansPlaque > 0) return;
    setEnCours(true);
    try {
      const reponse = await lotsAPI.addTrucks(lotId, lignesPourEnvoi(lignes), cle.current);
      cle.current = null;
      onDone?.(reponse);
      const refus = (reponse?.skipped || []).length + (reponse?.rejected || []).length;
      if (refus === 0) {
        const nombre = (reponse?.added || []).length;
        toast.success(nombre > 1 ? `${nombre} camions ajoutés` : 'Camion ajouté');
        onOpenChange(false);
      } else {
        setResultat(reponse);
      }
    } catch (err) {
      toast.error(messageErreur(err, "Les camions n'ont pas pu être ajoutés"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="lot-ajout-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter des camions{reference ? ` au lot ${reference}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">Coller ou saisir les camions à ajouter au lot.</DialogDescription>
        </DialogHeader>
        {resultat ? (
          <div className="space-y-3" data-testid="lot-ajout-resultat">
            <p className="text-sm text-foreground">
              {(resultat.added || []).length} camion{(resultat.added || []).length > 1 ? 's' : ''} ajouté
              {(resultat.added || []).length > 1 ? 's' : ''}.
            </p>
            <ListeRefus titre="Plaques ignorées" lignes={lignesRefus(resultat.skipped)} ton="neutre" />
            <ListeRefus titre="Lignes refusées" lignes={lignesRefus(resultat.rejected)} />
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Fermer</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={envoyer} className="min-w-0 space-y-4">
            <GrilleCamions lignes={lignes} onChange={setLignes} disabled={enCours} />
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={enCours || resume.total === 0 || resume.sansPlaque > 0}
                data-testid="lot-ajout-envoyer"
              >
                {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {resume.total > 0 ? `Ajouter ${resume.total} camion${resume.total > 1 ? 's' : ''}` : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AjouterCamionsDialog;
